import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTranslations, upsertTranslation, deleteTranslation, getLanguages } from '../../api';
import { PageHeader, EmptyState, SearchInput, TableSkeleton } from '../../components/ui';
import { Plus, Pencil, Trash2, Globe, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const MODULES = ['common', 'auth', 'home', 'astrologer', 'consultation', 'wallet', 'profile', 'settings'];

export default function TranslationsPage() {
  const qc = useQueryClient();
  const [module, setModule] = useState('common');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<any>(null);
  const [newKey, setNewKey] = useState('');
  const [newValues, setNewValues] = useState<Record<string, string>>({ en: '', hi: '' });

  const { data: langs } = useQuery({ queryKey: ['admin-langs'], queryFn: () => getLanguages().then((r) => r.data.data.languages) });
  const { data, isLoading } = useQuery({
    queryKey: ['admin-translations', module, search],
    queryFn: () => getTranslations({ module, search: search || undefined }).then((r) => r.data.data.translations),
  });

  const saveMutation = useMutation({
    mutationFn: (data: any) => upsertTranslation(data),
    onSuccess: () => { toast.success('Translation saved'); qc.invalidateQueries({ queryKey: ['admin-translations'] }); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTranslation(id),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['admin-translations'] }); },
  });

  const handleSave = (t: any) => {
    saveMutation.mutate({ module: t.module || module, key: t.key || newKey, values: t.values || newValues });
  };

  const translations = data || [];
  const languages = langs || [{ code: 'en', name: 'English' }, { code: 'hi', name: 'Hindi' }];

  return (
    <div className="p-6">
      <PageHeader title="Translations" subtitle="Manage multi-language content dynamically">
        <button onClick={() => setEditing({ isNew: true })} className="btn-primary"><Plus size={15} /> Add Translation</button>
      </PageHeader>

      {/* Filters */}
      <div className="card p-4 mb-5 flex flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap">
          {MODULES.map((m) => (
            <button key={m} onClick={() => setModule(m)} className={`px-3 py-1 rounded-lg text-xs font-medium border ${module === m ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              {m}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px]">
          <SearchInput value={search} onChange={setSearch} placeholder="Search keys..." />
        </div>
      </div>

      {/* New translation form */}
      {editing?.isNew && (
        <div className="card p-5 mb-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Plus size={16} /> Add New Translation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Module</label>
              <select className="input" value={module} onChange={(e) => setModule(e.target.value)}>
                {MODULES.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Key</label>
              <input className="input" value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="home.welcome.title" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {languages.map((lang: any) => (
              <div key={lang.code}>
                <label className="block text-xs text-gray-600 mb-1">{lang.name} ({lang.code})</label>
                <input className="input" value={newValues[lang.code] || ''} onChange={(e) => setNewValues({ ...newValues, [lang.code]: e.target.value })} placeholder={`Value in ${lang.name}`} />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={() => handleSave({ key: newKey, values: newValues })} disabled={!newKey || saveMutation.isPending} className="btn-primary btn-sm">
              <Save size={13} /> Save
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? <div className="p-5"><TableSkeleton /></div> : translations.length === 0 ? (
          <EmptyState message={`No translations for module "${module}"`} icon={<Globe size={32} />} />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">Key</th>
                {languages.map((l: any) => <th key={l.code} className="table-th">{l.name}</th>)}
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {translations.map((t: any) => (
                <tr key={t._id} className="table-row">
                  <td className="table-td font-mono text-xs">{t.key}</td>
                  {languages.map((l: any) => (
                    <td key={l.code} className="table-td text-xs max-w-[200px] truncate">
                      {editing?._id === t._id ? (
                        <input
                          className="input text-xs"
                          value={editing.values?.[l.code] || ''}
                          onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [l.code]: e.target.value } })}
                        />
                      ) : (t.values?.[l.code] || <span className="text-gray-300">—</span>)}
                    </td>
                  ))}
                  <td className="table-td">
                    {editing?._id === t._id ? (
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(null)} className="btn-secondary btn-sm text-xs py-1">Cancel</button>
                        <button onClick={() => handleSave(editing)} disabled={saveMutation.isPending} className="btn-primary btn-sm text-xs py-1"><Save size={12} /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <button onClick={() => setEditing({ ...t })} className="p-1.5 rounded hover:bg-gray-100 text-blue-500"><Pencil size={13} /></button>
                        <button onClick={() => { if (window.confirm('Delete?')) deleteMutation.mutate(t._id); }} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
